import React, { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/configureStore";
import Bugsnag from "@bugsnag/js";
import OT from "@opentok/client";
import PublisherPreview from "./PublisherPreview";
import { AlertRequest } from "../../../app/types";
import { useSession } from "../../api/helpers";
import {
  getAudioSources,
  getSelectedAudioSource,
  getSelectedVideoSource,
  getVideoSources,
} from "../../app/selectors";
import { addAlert, selectSource, setSources } from "../../app/uiSlice";
import CustomMaterialSelect from "../../common/CustomMaterialSelect";
import GradientButton from "../../common/GradientButton";
import GradientLine from "../../common/GradientLine";
import Padding from "../../common/Padding";
import PlainButton from "../../common/PlainButton";
import LogError from "../../logging/LogError";
import LightTheme from "../../theme/LightTheme";
import { getIsPublishing } from "../selectors";
import useSessionId from "../useSessionId";

interface Props {
  onViewOnly: () => void;
  onSetupComplete: () => void;
  vonageSession: OT.Session | undefined;
}

const deviceAccessAlert: AlertRequest = {
  title: "Access to Camera or Microphone Denied",
  msg: "Please allow access to camera and microphone and refresh the page",
};

const PublisherSetup = ({
  onViewOnly,
  onSetupComplete,
  vonageSession,
}: Props) => {
  const dispatch = useAppDispatch();
  const session = useSession();
  const sessionId = useSessionId();
  const isPublishing = useAppSelector(getIsPublishing(sessionId));
  const audioSources = useAppSelector(getAudioSources);
  const videoSources = useAppSelector(getVideoSources);
  const selectedAudioSource = useAppSelector(getSelectedAudioSource);
  const selectedVideoSource = useAppSelector(getSelectedVideoSource);

  useEffect(() => {
    OT.getUserMedia()
      .then(() => {
        OT.getDevices((e, devices) => {
          if (e) {
            LogError(e);
          } else if (devices !== undefined) {
            dispatch(setSources(devices));
          }
        });
      })
      .catch((reason) => {
        if (reason.name === "OT_USER_MEDIA_ACCESS_DENIED") {
          dispatch(addAlert(deviceAccessAlert));
        } else {
          Bugsnag.leaveBreadcrumb("Error retrieving User Media", reason);
          LogError(new Error("Error retrieving User Media"));
        }
      });
  }, [dispatch]);

  const onAudioChange = useCallback(
    (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const newSource = audioSources.find(
        (audio) => audio.deviceId === event.target.value,
      );
      if (newSource) {
        dispatch(selectSource(newSource));
      }
    },
    [audioSources, dispatch],
  );

  const onVideoChange = useCallback(
    (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const newSource = videoSources.find(
        (video) => video.deviceId === event.target.value,
      );
      if (newSource) {
        dispatch(selectSource(newSource));
      }
    },
    [dispatch, videoSources],
  );

  if (session === undefined) {
    return null;
  }

  return (
    <div className="stepper-container">
      <div className="stepper-left-column-container">
        <PublisherPreview />
      </div>
      <div className="stepper-right-column-container">
        <div>
          <p className="sessionTitle">{session.title}</p>
          <p className="setupTitle">Presentation Setup</p>
          <p className="label">Audio & Video</p>
          <GradientLine />
          <div className="optionsWrapper">
            <CustomMaterialSelect
              id="camera-selector"
              onChange={onVideoChange}
              label="Camera"
              value={selectedVideoSource?.deviceId}
              options={videoSources.map((video) => ({
                value: video.deviceId,
                label: video.label !== "" ? video.label : "Default",
              }))}
            />
            <CustomMaterialSelect
              id="audio-selector"
              onChange={onAudioChange}
              label="Audio"
              value={selectedAudioSource?.deviceId}
              options={audioSources.map((audio) => ({
                value: audio.deviceId,
                label: audio.label !== "" ? audio.label : "Default",
              }))}
            />
          </div>
        </div>
        <div className="action-bar">
          <PlainButton onClick={onViewOnly}>View Only</PlainButton>
          <Padding width={10} />
          <GradientButton
            disabled={vonageSession === undefined}
            onClick={onSetupComplete}
          >
            {isPublishing ? "Close" : "Present"}
          </GradientButton>
        </div>
      </div>
      <style jsx>{`
        .optionsWrapper {
          padding: 10px 0 0 0;
        }
        .label {
          color: #343434;
          font-weight: bolder;
        }
        .stepper-left-column-container {
          width: 53%;
          padding: 20px;
          aspect-ratio: 1.35;
        }

        .normal-text {
          font-size: 0.6rem;
          color: ${LightTheme.font.secondary.color};
        }
        .stepper-right-column-container {
          width: 47%;
          padding: 20px;
          background: rgba(128, 129, 145, 0.05);
          display: flex;
          flex-direction: column;
          align-content: space-between;
          justify-content: space-between;
        }
        .action-bar {
          display: flex;
          flex-direction: row;
          align-items: flex-end;
          justify-content: flex-end;
        }
        .sessionTitle {
          font-size: 0.7rem;
          color: #3a3a3b;
          padding: 0 0 5px 0;
        }
        .setupTitle {
          color: #343434;
          font-weight: bolder;
        }
        .stepper-container {
          position: absolute;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-self: center;
          width: 674px;
          border-radius: 10px;
          background: rgba(251, 251, 252, 1);
          box-shadow: 2px 2px 10px rgba(166, 171, 189, 0.5);
          border-top: double 3px transparent;
          background-origin: border-box;
          background-clip: content-box, border-box;
          z-index: 1;
        }
        p {
          margin: 0;
        }
        @media (max-width: 700px) {
          .stepper-container {
            flex-direction: column;
            width: 362px;
          }
          .stepper-right-column-container {
            width: 100%;
          }
          .stepper-left-column-container {
            width: 100%;
          }
          .sessionTitle {
            display: none;
          }
          .action-bar {
            margin-top: 20px;
          }
        }
        @media (max-width: 420px) {
          .stepper-container {
            width: 95%;
          }
        }
      `}</style>
    </div>
  );
};

export default PublisherSetup;
